import { useSelector } from 'react-redux';
import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Skeleton, Grid, Paper, Typography, alpha, Box, Button,
} from '@mui/material';
import {
  DirectionsCar, LocalShipping, HomeRepairService,
  Payments, Assessment, Settings, ArrowForward, People, Business,
} from '@mui/icons-material';
import api from '../../services/api';
import { selectUser, selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectEnabledModules, selectTenantSlug } from '../../store/slices/tenantSlice';
import type { RootState } from '../../store';
import PageShell from '../../components/layout/PageShell';
import DashboardRenderer from '../../components/dashboard/DashboardRenderer';
import DashboardStatsGrid from '../../components/dashboard/DashboardStatsGrid';
import DashboardAnalyticsCharts from '../../components/dashboard/DashboardAnalyticsCharts';
import DashboardRecentActivity from '../../components/dashboard/DashboardRecentActivity';
import DashboardOpsAlerts from '../../components/dashboard/DashboardOpsAlerts';
import DashboardPeriodReports from '../../components/dashboard/DashboardPeriodReports';
import DashboardGodViewHeader from '../../components/dashboard/DashboardGodViewHeader';
import DashboardGodViewAnalytics from '../../components/dashboard/DashboardGodViewAnalytics';
import DashboardSection from '../../components/dashboard/DashboardSection';
import DeliveryPartnerFlowGuide from '../../components/delivery/DeliveryPartnerFlowGuide';
import VendorHubQuickActions from '../../components/vendor/VendorHubQuickActions';
import { getDashboardForRole } from '../../services/platform/runtimeService';
import { getDashboardRoleConfig, hasSection } from '../../config/dashboardRoleConfig';
import { useI18n } from '../../i18n/I18nProvider';
import { navI18nKey } from '../../i18n/navLabels';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { useDashboardLiveRefresh, DASHBOARD_POLL_MS } from '../../hooks/useDashboardLiveRefresh';

const QUICK_LINKS = [
  { label: 'Dispatcher', path: '/dispatcher', icon: DirectionsCar, module: 'transportation', color: '#dc2626' },
  { label: 'Transport', path: '/hubs/transport/transport/bookings', icon: DirectionsCar, module: 'transportation', color: '#2563eb' },
  { label: 'Porter', path: '/hubs/delivery/porter-service/bookings', icon: LocalShipping, module: 'porter', color: '#0f766e' },
  { label: 'Services', path: '/hubs/on_demand/home-services/bookings', icon: HomeRepairService, module: 'on_demand', color: '#0891b2' },
  { label: 'Zones', path: '/zones', icon: Settings, color: '#0284c7' },
  { label: 'Settlements', path: '/settlements', icon: Payments, color: '#0284c7' },
  { label: 'Reports', path: '/reports', icon: Assessment, color: '#0d9488' },
  { label: 'Master Data', path: '/config/masters', icon: Settings, color: '#64748b' },
];

const ROLE_ACCENTS: Record<string, string> = {
  super_admin: '#6366f1',
  client_admin: '#2563eb',
  employee: '#0891b2',
  driver: '#2563eb',
  delivery_partner: '#ea580c',
  merchant: '#059669',
  service_provider: '#7c3aed',
  vendor: '#db2777',
  customer: '#64748b',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const user = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);
  const tenantSlug = useSelector(selectTenantSlug);
  const tenantCompanyName = useSelector((s: RootState) => s.tenant.companyName);
  const role = user?.userType || 'client_admin';
  const roleConfig = getDashboardRoleConfig(role);
  const accent = ROLE_ACCENTS[role] || '#2563eb';
  const { isBackendUp } = useBackendHealth();
  const pollInterval = isBackendUp ? DASHBOARD_POLL_MS : false;
  const needsLiveRefresh = hasSection(roleConfig, 'godHeader')
    || hasSection(roleConfig, 'statsGrid')
    || hasSection(roleConfig, 'opsAlerts')
    || hasSection(roleConfig, 'godAnalytics')
    || hasSection(roleConfig, 'recentActivity')
    || hasSection(roleConfig, 'periodReports');

  useDashboardLiveRefresh(needsLiveRefresh && isBackendUp);

  const [revenueMixFrom, setRevenueMixFrom] = useState<string | null>(null);
  const [revenueMixTo, setRevenueMixTo] = useState<string | null>(null);
  const handleRevenueMixDateChange = useCallback((from: string | null, to: string | null) => {
    setRevenueMixFrom(from);
    setRevenueMixTo(to);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', tenantSlug],
    queryFn: async () => (await api.get('/dashboard')).data.data,
    refetchInterval: needsLiveRefresh ? pollInterval : false,
    enabled: isBackendUp,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics', tenantSlug],
    queryFn: async () => (await api.get('/analytics')).data.data,
    enabled: isBackendUp && (hasSection(roleConfig, 'trendCharts') || hasSection(roleConfig, 'godAnalytics')),
    refetchInterval: needsLiveRefresh ? pollInterval : false,
  });

  const { data: godView, isLoading: godLoading } = useQuery({
    queryKey: ['god-view', tenantSlug, role, enabledModules.join(','), revenueMixFrom, revenueMixTo],
    queryFn: async () => {
      const params: Record<string, string> = { role };
      if (revenueMixFrom) params.from = revenueMixFrom;
      if (revenueMixTo) params.to = revenueMixTo;
      return (await api.get('/analytics/god-view', { params })).data.data;
    },
    enabled: isBackendUp && (hasSection(roleConfig, 'godHeader') || hasSection(roleConfig, 'godAnalytics')),
    refetchInterval: needsLiveRefresh ? pollInterval : false,
  });

  const { data: roleDashboard, isLoading: dashLoading } = useQuery({
    queryKey: ['role-dashboard', tenantSlug, role],
    queryFn: () => getDashboardForRole(role),
    enabled: hasSection(roleConfig, 'roleWidgets'),
  });

  const overview = data?.overview || {};

  const links = useMemo(() => QUICK_LINKS.filter((l) => {
    if (!l.module) return true;
    return isSuperAdmin || enabledModules.includes(l.module);
  }), [enabledModules, isSuperAdmin]);

  const welcomeName = isSuperAdmin
    ? (tenantCompanyName || tenantSlug || t('admin.page.dashboard', 'Admin'))
    : (user?.firstName || t('admin.page.dashboard', 'Admin'));
  const headerTitle = `${t('admin.page.dashboard_welcome', 'Welcome')}, ${welcomeName}`;

  return (
    <PageShell>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        {hasSection(roleConfig, 'partnerGuide') && <DeliveryPartnerFlowGuide />}
        {role === 'merchant' && <VendorHubQuickActions />}

        {hasSection(roleConfig, 'godHeader') && (
          <DashboardGodViewHeader
            title={headerTitle}
            subtitle={roleConfig.subtitle}
            eyebrow={roleConfig.eyebrow}
            liveOps={godView?.liveOps}
            overview={overview}
            enabledModules={enabledModules}
            isSuperAdmin={isSuperAdmin}
            role={role}
            loading={godLoading || isLoading}
            accent={accent}
          />
        )}

        {hasSection(roleConfig, 'statsGrid') && (
          <DashboardSection title="Platform overview" subtitle="Customers, revenue, zones, and service volumes" accent={accent}>
            <DashboardStatsGrid
              overview={overview}
              loading={isLoading}
              enabledModules={enabledModules}
              isSuperAdmin={isSuperAdmin}
              role={role}
              embedded
            />
          </DashboardSection>
        )}

        {hasSection(roleConfig, 'opsAlerts') && (
          <DashboardSection title="Action items" subtitle="Operations that need your attention" accent="#dc2626">
            <DashboardOpsAlerts
              overview={overview}
              enabledModules={enabledModules}
              isSuperAdmin={isSuperAdmin}
              role={role}
              embedded
            />
          </DashboardSection>
        )}

        {hasSection(roleConfig, 'roleWidgets') && (
          dashLoading ? (
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3, mb: 3 }} />
          ) : roleDashboard?.layout?.length ? (
            <DashboardSection
              title={roleDashboard.name || `${roleConfig.eyebrow} overview`}
              subtitle="Role-specific metrics and activity"
              accent={accent}
            >
              <DashboardRenderer
                dashboard={roleDashboard}
                overview={overview}
                analytics={analytics}
                dashboardData={data}
                loading={isLoading}
                editable={false}
              />
            </DashboardSection>
          ) : null
        )}

        {hasSection(roleConfig, 'godAnalytics') && (
          <DashboardGodViewAnalytics
            data={godView}
            loading={godLoading}
            revenueMixFrom={revenueMixFrom}
            revenueMixTo={revenueMixTo}
            onRevenueMixDateChange={handleRevenueMixDateChange}
          />
        )}

        {hasSection(roleConfig, 'periodReports') && <DashboardPeriodReports />}

        {hasSection(roleConfig, 'trendCharts') && (
          <DashboardSection title="30-day service trends" subtitle="Transport and delivery volume and revenue" accent="#2563eb">
            <DashboardAnalyticsCharts analytics={analytics} loading={analyticsLoading} embedded />
          </DashboardSection>
        )}

        {hasSection(roleConfig, 'recentActivity') && (
          <DashboardSection title="Recent activity" subtitle="Latest transport and delivery jobs" accent="#64748b">
            <DashboardRecentActivity dashboardData={data} loading={isLoading} embedded />
          </DashboardSection>
        )}

        {hasSection(roleConfig, 'quickLinks') && (
          <DashboardSection title="Quick access" subtitle="Jump to key admin areas" accent="#64748b">
            <Grid container spacing={2}>
              {links.map((link) => {
                const Icon = link.icon;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={link.path}>
                    <Paper
                      elevation={0}
                      component={RouterLink}
                      to={link.path}
                      sx={{
                        p: 2.25,
                        borderRadius: 3,
                        border: `1px solid ${alpha(link.color, 0.15)}`,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.18s ease',
                        bgcolor: alpha(link.color, 0.04),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        '&:hover': {
                          borderColor: alpha(link.color, 0.4),
                          boxShadow: `0 8px 24px ${alpha(link.color, 0.18)}`,
                          transform: 'translateY(-3px)',
                          bgcolor: alpha(link.color, 0.07),
                        },
                      }}
                    >
                      <Box sx={{
                        p: 1.25,
                        borderRadius: '12px',
                        bgcolor: alpha(link.color, 0.12),
                        border: `1px solid ${alpha(link.color, 0.18)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 42,
                        height: 42,
                        color: link.color,
                      }}>
                        <Icon sx={{ fontSize: '1.25rem' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.84rem' }}>
                          {t(navI18nKey(link.label), link.label)}
                        </Typography>
                        <ArrowForward sx={{ fontSize: 13, color: 'text.disabled', mt: 0.25 }} />
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
              {isSuperAdmin && (
                <Grid item xs={6} sm={4} md={3} lg={2}>
                  <Paper
                    elevation={0}
                    component={RouterLink}
                    to="/tenants"
                    sx={{
                      p: 2.25,
                      borderRadius: 3,
                      border: `1px solid ${alpha('#6366f1', 0.18)}`,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'inherit',
                      bgcolor: alpha('#6366f1', 0.04),
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${alpha('#6366f1', 0.18)}`,
                        transform: 'translateY(-3px)',
                        bgcolor: alpha('#6366f1', 0.07),
                        borderColor: alpha('#6366f1', 0.4),
                      },
                    }}
                  >
                    <Box sx={{
                      p: 1.25, borderRadius: '12px', bgcolor: alpha('#6366f1', 0.12),
                      border: `1px solid ${alpha('#6366f1', 0.18)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 42, height: 42, color: '#6366f1',
                    }}>
                      <Business sx={{ fontSize: '1.25rem' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.84rem' }}>Tenants</Typography>
                      <ArrowForward sx={{ fontSize: 13, color: 'text.disabled', mt: 0.25 }} />
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DashboardSection>
        )}

        {isSuperAdmin && hasSection(roleConfig, 'godAnalytics') && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<People />} onClick={() => navigate('/tenants')}>
              Manage Tenants
            </Button>
          </Box>
        )}
      </Box>
    </PageShell>
  );
}
